/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { screensMap } from '..';
import { getPlanStatus } from '../EmiPlans/helper';
import PlanDescription from './Plan';
import { useGlobalStore } from '@/store';
import Input from '@/lib/Input';
import Button from '@/lib/Button';
import { OTP_REGEX } from '@/constants';
import SecuredByRazorpay from '@/lib/SecuredByRazorpay';
import {
  authorizePayment,
  fetchKfsStatement,
  initiatePayment,
  resendPaymentOTP,
  updatePayment,
} from '@/api/payment';
import { formRequest } from '@/utils/helpers';
import Modal from '@/lib/Modal';
import CheckBox from '@/lib/CheckBox';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { EmiPlans } from '@/api/types';
import Icon from '@/lib/Icon/Icon';
import useDeviceDetection from '@/hooks/useDeviceDetection';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const Payments = (): JSX.Element => {
  const { paymentDetails, selectedTenure, user, lenderDetails, userAccessToken } = useGlobalStore();
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { amount } = paymentDetails.data ?? {};
  const { contact } = user;
  const [secondsToResendOtp, setSecondsToResendOtp] = useState(15);
  const [otp, setOtp] = useState('');
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const reduceResendInterval = useRef<NodeJS.Timer | null>(null);
  const [paymentInitiateError, setPaymentError] = useState(false);
  const [showKfs, setShowKfs] = useState(false);
  const [kfsBlob, setkfsBlob] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>();
  const [isConsentGiven, setIsConsetGiven] = useState(true);
  const device = useDeviceDetection();

  const [state, setState] = useState({
    fetchingKfs: false,
    kfsError: false,
  });

  const OTP_INAVLID_ERROR_CODE = 'CBNPL00005';

  const resetResendOTPTimer = (): void => {
    setSecondsToResendOtp(15);

    reduceResendInterval.current = setInterval(() => {
      setSecondsToResendOtp((current) => {
        if (current > 0) {
          return current - 1;
        } else {
          clearTimeout(reduceResendInterval.current as NodeJS.Timer);
          return current;
        }
      });
    }, 1000);
  };

  const handleInitiatePayment = async (): Promise<void> => {
    try {
      if (selectedTenure) {
        const resp = await initiatePayment(selectedTenure);
        if (!resp.success) {
          setPaymentError(true);
        }
      }
    } catch (err) {
      // catch error
      setPaymentError(true);
    }
  };

  useEffect(() => {
    handleInitiatePayment();
    resetResendOTPTimer();

    // Track payment initiation render
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.PAYMENT_INITIATION_SCREEN_RENDER,
      screen: screensMap.PAYMENTS,
      properties: {
        contact: user.contact,
        fc_customer_id: user.id,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
        selected_plan: selectedTenure,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });

    return () => {
      clearTimeout(reduceResendInterval.current as NodeJS.Timer);
    };
  }, []);

  const resendOtp = async (): Promise<void> => {
    if (secondsToResendOtp === 0 && contact) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response = await resendPaymentOTP();
      if (response.success) {
        resetResendOTPTimer();
      }
    }
  };

  const handleOtpInput = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    if (value.length > 6) {
      return;
    }
    setOtp(value);
    setOtpError('');
  };

  const submitOtp = async (): Promise<void> => {
    if (!isConsentGiven) {
      return;
    }

    if (isSubmittingOtp) {
      return;
    }

    // Track Submit opt clicked
    trackHelper({
      actionName: ANALYTICS_ACTIONS.CLICK,
      objectName: EVENT_NAMES.SUBMIT_OTP_CLICKED,
      screen: screensMap.PAYMENTS,
    });

    if (OTP_REGEX.test(otp)) {
      setIsSubmittingOtp(true);

      await authorizePayment({
        otp,
      })
        .then((res) => {
          if (res.data.gateway_payload) {
            const { url, payload, method } = res.data.gateway_payload;
            formRequest(url, payload, method);
          }
          // Track payment success
          trackHelper({
            actionName: ANALYTICS_ACTIONS.BACKEND_RESPONSE,
            objectName: EVENT_NAMES.PAYMENT_SUCCESS,
            screen: screensMap.PAYMENTS,
            properties: {
              contact: user.contact,
              fc_customer_id: user.id,
              user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
              selected_plan: selectedTenure,
              user_type: getUserLoggedInStatus(userAccessToken, user.contact),
            },
          });
        })
        .catch(async (er) => {
          setShowKfs(false);
          if (er?.details && er?.details.length && er?.details[0].code === OTP_INAVLID_ERROR_CODE) {
            setOtpError('OTP incorrect. Please enter correct OTP.');
            setIsSubmittingOtp(false);
            trackHelper({
              actionName: ANALYTICS_ACTIONS.BACKEND_RESPONSE,
              objectName: EVENT_NAMES.PAYMENT_FAILURE,
              screen: screensMap.PAYMENTS,
              properties: {
                contact: user.contact,
                fc_customer_id: user.id,
                user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
                selected_plan: selectedTenure,
                user_type: getUserLoggedInStatus(userAccessToken, user.contact),
                failure_reason: 'OTP incorrect',
              },
            });
          } else {
            try {
              setIsSubmittingOtp(false);
              const udpateRes = await updatePayment({
                status: 'failed',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                failure_reason: 'expired',
              });
              if (udpateRes.data.gateway_payload) {
                const { url, payload, method } = udpateRes.data.gateway_payload;
                formRequest(url, payload, method);
              }
            } catch (error2: unknown) {
              setIsSubmittingOtp(false);
            }
          }
        });
    } else {
      setOtpError('Invalid OTP');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      // submit otp on enter press
      submitOtp();
    }
  };

  const downloadFile = (): void => {
    const downloadLink = document.createElement('a');
    const fileName = 'kfs.pdf';
    downloadLink.href = kfsBlob as string;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const getKfsStatement = async (): Promise<void> => {
    try {
      setState({
        ...state,
        fetchingKfs: true,
      });
      const res = await fetchKfsStatement();
      if (res.data?.file_data) {
        setShowKfs(true);
        setkfsBlob(`data:application/pdf;base64,${res.data.file_data}`);
        // Track payment initiation render
        trackHelper({
          actionName: ANALYTICS_ACTIONS.RENDER,
          objectName: EVENT_NAMES.KFS_SHOWN,
          screen: screensMap.PAYMENTS,
          properties: {
            contact: user.contact,
            fc_customer_id: user.id,
            user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
            selected_plan: selectedTenure,
            user_type: getUserLoggedInStatus(userAccessToken, user.contact),
          },
        });
      }
      setState({
        ...state,
        fetchingKfs: false,
      });

      if (!res.success) {
        setState({
          ...state,
          kfsError: true,
        });
      }
    } catch (err) {
      setState({
        ...state,
        kfsError: true,
      });
    }
  };

  const handleCompletePayment = async (): Promise<void> => {
    if (OTP_REGEX.test(otp)) {
      await getKfsStatement();
    } else {
      setOtpError('Invalid OTP');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };

  return (
    <>
      <div className="d:h-full d:flex d:items-center d:justify-center">
        <div className="d:w-70">
          <div className="py-4 px-6 h-full grow overflow-scroll d:p-0">
            <p className="d:block hidden mb-6 text-2xl text-on-grey-normal font-semibold">
              Complete Payment
            </p>
            <PlanDescription />
            <p className="mt-6 text-base font-medium text-on-grey-subtle mb-3">
              Enter OTP sent to {contact}
            </p>
            <Input
              maxLength={6}
              error={otpError}
              value={otp}
              type="number"
              onChange={handleOtpInput}
              onKeyDown={handleKeyDown}
              placeholder="Enter 6 digit OTP"
            />
            <div className="flex w-full justify-center">
              {secondsToResendOtp > 0 ? (
                <p className="mt-4 text-base text-on-grey-muted">
                  Resend OTP in : 00:{secondsToResendOtp}s
                </p>
              ) : (
                <button onClick={resendOtp} className="text-base text-on-grey-muted cursor-pointer">
                  Resend OTP
                </button>
              )}
            </div>
          </div>
          <div className="shrink-0 align-bottom w-full">
            <div className="shadow-top px-6 absolute bottom-0 d:relative w-full d:shadow-none d:p-0 d:mt-3">
              <div className="flex items-center w-full justify-between d:justify-center">
                <p className="text-base text-on-gray-normal font-semibold d:hidden ">
                  ₹{Number(amount) / 100}
                </p>
                <Button
                  isLoading={state.fetchingKfs}
                  onClick={handleCompletePayment}
                  loadingText="Processing"
                  className="[&&]:w-3/4 [&&]:d:w-full"
                >
                  Proceed
                </Button>
              </div>
              <SecuredByRazorpay />
            </div>
          </div>
        </div>
      </div>
      {paymentInitiateError || state.kfsError ? (
        <PaymentCancelWrapper
          isOpen={paymentInitiateError || state.kfsError}
          onDismiss={(): void => {
            setPaymentError(false);
            setState({
              ...state,
              kfsError: false,
            });
          }}
          title={state.kfsError ? 'Error fetching Key Fact Statement' : 'Error initiating payment'}
          description="Something went wrong, Please try again!"
          hideCta={true}
        />
      ) : (
        ''
      )}
      {showKfs && kfsBlob ? (
        <Modal
          isOpen={showKfs}
          onDismiss={(): void => {
            setShowKfs(false);
          }}
        >
          <Modal.Body>
            <div className="d:w-70">
              <div className="flex items-center">
                <p className="font-semibold text-xl text-on-grey-normal mr-3">Loan Agreement</p>
                <button onClick={downloadFile}>
                  <Icon width={24} fill="#D92D20" name="download" />
                </button>
              </div>
              <div className="max-h-96 overflow-y-scroll mt-4">
                <Document onLoadSuccess={onDocumentLoadSuccess} file={`${kfsBlob}`}>
                  {Array.from(new Array(numPages), (el, index) => (
                    <Page
                      className="page-pdf"
                      key={`page_${index + 1}`}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={device === 'Desktop' ? 680 : 320}
                      pageIndex={index}
                    />
                  ))}
                </Document>
              </div>
              <div
                onClick={(): void => {
                  setIsConsetGiven(!isConsentGiven);
                }}
                className="flex my-3 items-center"
              >
                <CheckBox
                  checked={isConsentGiven}
                  onClick={(): void => {
                    setIsConsetGiven(!isConsentGiven);
                  }}
                />
                <p className="text-sm text-on-grey-subtle">
                  I confirm and accept the agreement’s terms and conditions
                </p>
              </div>
              <Button
                isLoading={isSubmittingOtp}
                onClick={submitOtp}
                loadingText="Processing"
                className="w-full mt-0 mb-0"
                disabled={!isConsentGiven || !kfsBlob}
              >
                Proceed & complete payment
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      ) : null}
    </>
  );
};

export default Payments;
